/// <reference path="../../../../core/extensions/string/string.extension.d.ts" />
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, ViewChild, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import '../../../../core/extensions/string/string.extension';
import { DocumentModel, DocumentNodeModel } from '../../../../core/models/data/document.model';
import { AttachmentSelectionState, AttachmentSource, EmailAttachmentItem } from '../../../../core/models/ui/email-attachment.model';
import { DocumentService } from '../../../../core/services/document.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { DocumentUploadManagerService, UploadItem } from '../../../documents/services/document-upload-manager.service';
import { DocumentPermission, DocumentStatus, DocumentUploadStatus } from '../../../../core/enums/data.enums';
import { DocumentListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ModalActions } from '../../../../core/enums/base.enums';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { FolderTreePickerComponent, FolderTreeSelection } from '../../../../parts/folder-tree-picker/folder-tree-picker.component';
import { DocumentFilePickerComponent } from '../../../../parts/document-file-picker/document-file-picker.component';
import { DocumentStatusUtilities } from '../../../../core/utilities/document-status.utilities';
import { CommunicationsConstants } from '../../../../core/constants/communications.constants';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { CommunicationsMessagingConstants } from '../../../../core/constants/messaging.constants';

/**
 * Attachment picker for the mass-email composer. Two ways to attach a file, both resolving to a
 * Document Center file so the send only carries ids:
 *   - Upload from device — committed to the org's Email Attachments folder (or another folder the user
 *     picks) via the shared DocumentUploadManagerService, which enforces per-file/storage limits + scanning.
 *   - Attach from Document Center — pick an existing file from any folder.
 * Enforces a combined 20 MB ceiling client-side and emits the committed attachment set to the page.
 */
@Component({
    selector: 'ms-email-attachments',
    standalone: true,
    imports: [
        CommonModule,
        FontAwesomeModule,
        EscCloseDirective,
        FolderTreePickerComponent,
        DocumentFilePickerComponent
    ],
    templateUrl: './email-attachments.component.html',
    styleUrl: './email-attachments.component.scss',
    providers: [DocumentUploadManagerService]
})
export class EmailAttachmentsComponent {

    public organizationId = input.required<number>();

    public attachmentsChanged = output<AttachmentSelectionState>();

    private documentService = inject(DocumentService);
    private uploadManager = inject(DocumentUploadManagerService);
    private bootstrapService = inject(BootstrapService);
    private destroyRef = inject(DestroyRef);

    public readonly maxTotalLabel = CommunicationsConstants.MAX_TOTAL_ATTACHMENT_LABEL();
    public readonly storageNote = CommunicationsMessagingConstants.ATTACHMENTS_STORAGE_NOTE();
    public readonly filePickerModalId = CommunicationsConstants.ATTACHMENT_FILE_PICKER_MODAL_ID();
    public readonly uploadFolderModalId = CommunicationsConstants.ATTACHMENT_UPLOAD_FOLDER_MODAL_ID();

    public DocumentStatus = DocumentStatus;
    public DocumentUploadStatus = DocumentUploadStatus;

    public attachments = signal<EmailAttachmentItem[]>([]);
    public errorMessage = signal<string>('');

    // Upload target folder. Defaults to the org's Email Attachments folder; the user can redirect uploads
    // to any Document Center folder via the change-folder picker.
    public targetFolderName = signal<string>(DocumentsConstants.EMAIL_ATTACHMENTS_FOLDER_NAME());
    private explicitTargetFolderId = signal<number | undefined>(undefined);
    private defaultFolderIdCache?: number;

    // Change-folder picker state.
    public uploadFolderTree = signal<DocumentNodeModel[]>([]);
    public selectedUploadFolder = signal<FolderTreeSelection>(undefined);
    public isLoadingUploadFolders = signal<boolean>(false);
    public isApplyingUploadFolder = signal<boolean>(false);
    public canApplyUploadFolder = computed<boolean>(() =>
        typeof this.selectedUploadFolder() === 'number' && !this.isApplyingUploadFolder());

    @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;
    @ViewChild(DocumentFilePickerComponent) private filePicker?: DocumentFilePickerComponent;

    // The manager keeps every upload row (active + terminal): active ones render as progress, failed
    // ones as retryable errors, and committed (Done) ones become attachments via the completed stream.
    public uploadItems = this.uploadManager.items;

    public activeUploads = computed<UploadItem[]>(() =>
        this.uploadItems().filter(i => i.status === DocumentUploadStatus.Pending || i.status === DocumentUploadStatus.Uploading));
    public failedUploads = computed<UploadItem[]>(() =>
        this.uploadItems().filter(i => i.status === DocumentUploadStatus.Failed));
    public isUploading = computed<boolean>(() => this.activeUploads().length > 0);

    private committedBytes = computed<number>(() => this.attachments().reduce((sum, a) => sum + a.sizeBytes, 0));
    private inFlightBytes = computed<number>(() => this.activeUploads().reduce((sum, i) => sum + i.sizeBytes, 0));
    public totalBytes = computed<number>(() => this.committedBytes() + this.inFlightBytes());
    public hasAttachments = computed<boolean>(() => this.attachments().length > 0);

    constructor() {
        // A file finishes uploading + committing → surface it as an attachment (the manager already
        // applied the per-file/storage checks and started the virus scan).
        this.uploadManager.completed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(event => this.onUploadCompleted(event.document));

        this.uploadManager.batchError
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(message => this.errorMessage.set(message));

        // Live-update the "Scanning" badge: poll each Pending attachment until its scan clears (Available
        // → badge clears) or it's quarantined (→ removed with an error), via the shared utility.
        DocumentStatusUtilities.pollResolvedStatuses(this.documentService, () => this.pendingAttachmentIds())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(resolved => this.applyResolvedStatuses(resolved));

        // Keep the page in sync with the committed attachment set and upload activity.
        effect(() => this.emitState());
    }

    //#region Upload from device

    public triggerFileInput(): void {
        this.fileInput?.nativeElement.click();
    }

    public async onDeviceFilesSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const files = input.files ? Array.from(input.files) : [];
        // Clear the value so selecting the same file again still fires (change).
        input.value = '';
        if (files.length === 0) {
            return;
        }

        this.errorMessage.set('');

        // Reject the whole batch if it wouldn't fit under the combined ceiling — nothing is uploaded.
        const batchBytes = files.reduce((sum, file) => sum + file.size, 0);
        const remaining = CommunicationsConstants.MAX_TOTAL_ATTACHMENT_BYTES() - this.totalBytes();
        if (batchBytes > remaining) {
            this.errorMessage.set(CommunicationsMessagingConstants.ATTACHMENT_TOTAL_LIMIT_EXCEEDED().replaceValuesWithObject({
                name: files.length === 1 ? files[0].name : `${files.length} files`,
                limit: CommunicationsConstants.MAX_TOTAL_ATTACHMENT_LABEL()
            }));
            return;
        }

        const folderId = await this.resolveTargetFolderId();
        if (folderId == null) {
            this.errorMessage.set(CommunicationsMessagingConstants.ATTACHMENTS_FOLDER_RESOLVE_FAILED());
            return;
        }

        this.uploadManager.enqueue(files, folderId);
    }

    public retryUpload(tempId: number): void {
        this.uploadManager.retry(tempId);
    }

    public dismissUpload(tempId: number): void {
        this.uploadManager.cancel(tempId);
    }

    private onUploadCompleted(document: DocumentModel): void {
        this.addAttachment({
            documentId: document.documentId,
            name: document.name,
            sizeBytes: document.sizeBytes,
            source: AttachmentSource.Upload,
            status: document.status
        });
    }

    //#endregion Upload from device

    //#region Attach from Document Center

    public openFilePicker(): void {
        this.errorMessage.set('');
        void this.filePicker?.open();
    }

    public onDocumentPicked(node: DocumentNodeModel): void {
        this.addAttachment({
            documentId: node.documentNodeId,
            name: node.name,
            sizeBytes: node.sizeBytes ?? 0,
            source: AttachmentSource.DocumentCenter,
            status: node.status
        });
    }

    //#endregion Attach from Document Center

    //#region Change upload folder

    public async openChangeFolder(): Promise<void> {
        this.selectedUploadFolder.set(undefined);
        this.isLoadingUploadFolders.set(true);
        const result = await this.documentService.getChildren(undefined, DocumentListSortOptions.NameAsc);
        this.isLoadingUploadFolders.set(false);
        this.uploadFolderTree.set(result.success ? result.data ?? [] : []);
        this.bootstrapService.manageModal(`#${this.uploadFolderModalId}`, ModalActions.Show);
    }

    public loadUploadFolderChildren = (folderId: number): Promise<DocumentNodeModel[]> =>
        this.documentService.getChildren(folderId, DocumentListSortOptions.NameAsc)
            .then(result => (result.success ? result.data ?? [] : []));

    public async confirmUploadFolder(): Promise<void> {
        const selection = this.selectedUploadFolder();
        if (typeof selection !== 'number') {
            return;
        }
        this.isApplyingUploadFolder.set(true);
        const result = await this.documentService.getFolderById(selection);
        this.isApplyingUploadFolder.set(false);
        if (result.success && result.data) {
            this.explicitTargetFolderId.set(result.data.folderId);
            this.targetFolderName.set(result.data.name);
        }
        this.closeChangeFolder();
    }

    public closeChangeFolder(): void {
        this.bootstrapService.manageModal(`#${this.uploadFolderModalId}`, ModalActions.Hide);
    }

    //#endregion Change upload folder

    public removeAttachment(documentId: number): void {
        this.attachments.update(list => list.filter(a => a.documentId !== documentId));
        this.errorMessage.set('');
    }

    public reset(): void {
        this.attachments.set([]);
        this.errorMessage.set('');
        this.explicitTargetFolderId.set(undefined);
        this.targetFolderName.set(DocumentsConstants.EMAIL_ATTACHMENTS_FOLDER_NAME());
        this.uploadManager.cancelAll();
    }

    public formatBytes(bytes: number | undefined): string {
        return this.documentService.formatBytes(bytes);
    }

    private addAttachment(item: EmailAttachmentItem): void {
        if (this.attachments().some(a => a.documentId === item.documentId)) {
            this.errorMessage.set(CommunicationsMessagingConstants.ATTACHMENT_ALREADY_ATTACHED()
                .replaceValuesWithObject({ name: item.name }));
            return;
        }
        // Guard the ceiling for picked (already-stored) files; uploaded files were pre-checked before enqueue.
        if (this.committedBytes() + item.sizeBytes > CommunicationsConstants.MAX_TOTAL_ATTACHMENT_BYTES()) {
            this.errorMessage.set(CommunicationsMessagingConstants.ATTACHMENT_TOTAL_LIMIT_EXCEEDED().replaceValuesWithObject({
                name: item.name,
                limit: CommunicationsConstants.MAX_TOTAL_ATTACHMENT_LABEL()
            }));
            return;
        }
        this.attachments.update(list => [...list, item]);
    }

    private pendingAttachmentIds(): number[] {
        return this.attachments()
            .filter(a => a.status === DocumentStatus.Pending)
            .map(a => a.documentId);
    }

    // Apply resolved scan statuses: Available clears the "Scanning" badge in place; Quarantined drops the
    // attachment so it can't be sent, and names which file was removed.
    private applyResolvedStatuses(resolved: Map<number, DocumentStatus>): void {
        const quarantined = this.attachments().filter(a => resolved.get(a.documentId) === DocumentStatus.Quarantined);

        this.attachments.update(list => list
            .filter(a => resolved.get(a.documentId) !== DocumentStatus.Quarantined)
            .map(a => (resolved.has(a.documentId) ? { ...a, status: resolved.get(a.documentId) } : a)));

        if (quarantined.length > 0) {
            this.errorMessage.set(CommunicationsMessagingConstants.ATTACHMENT_QUARANTINED_REMOVED()
                .replaceValuesWithObject({ name: quarantined.map(a => a.name).join(', ') }));
        }
    }

    private emitState(): void {
        // Report the same total the component enforces and displays (committed + in-flight uploads) so
        // the emitted state never disagrees with the picker UI.
        const totalBytes = this.totalBytes();
        this.attachmentsChanged.emit({
            attachments: this.attachments(),
            totalBytes,
            isWithinLimit: totalBytes <= CommunicationsConstants.MAX_TOTAL_ATTACHMENT_BYTES(),
            isUploading: this.isUploading()
        });
    }

    private async resolveTargetFolderId(): Promise<number | undefined> {
        const explicit = this.explicitTargetFolderId();
        if (explicit != null) {
            return explicit;
        }
        if (this.defaultFolderIdCache != null) {
            return this.defaultFolderIdCache;
        }
        const folderId = await this.findOrCreateEmailAttachmentsFolder();
        this.defaultFolderIdCache = folderId ?? undefined;
        return folderId;
    }

    // The backend provisions a locked, Admin Only "Email Attachments" folder at org creation. Resolve
    // it by name; if it isn't there yet (provisioning not run), fall back to creating an Admin Only
    // folder so uploads still work. The backend owns the locked/name/permission semantics.
    private async findOrCreateEmailAttachmentsFolder(): Promise<number | undefined> {
        const organizationId = this.organizationId();
        const name = DocumentsConstants.EMAIL_ATTACHMENTS_FOLDER_NAME();

        const existing = await this.documentService.filterFolders({ organizationId, searchTerm: name });
        if (existing.success) {
            const match = existing.data?.data?.find(f => f.parentFolderId == null && f.name.toLowerCase() === name.toLowerCase());
            if (match) {
                return match.folderId;
            }
        }

        const created = await this.documentService.createFolder({ organizationId, name, permission: DocumentPermission.AdminOnly });
        return created.success ? created.data?.folderId : undefined;
    }
}
